// Bookingmotoren: pris, rabatkoder, medlemspris, gebyrer og ledige tider – ud fra et udsnit af klientens data.
// Samme regler i testversionen (data i browseren) og på serveren (data fra databasen), så prisen og tiderne,
// kunden ser, altid er dem, der bookes. Ingen afhængigheder uden for src/core.
import { fejl } from './fejl.ts';
import type { Biltype, Booking, BookingStatus, Fravaer, Kupon, Lukkedag, SaerligDag, Steamer, Tilvalg, Ydelse, YdelsePris, Zone, Arbejdstid } from './typer.ts';
import { beregnPris, kuponFejl, type Prisberegning, type ValgtTilvalg } from './pris.ts';
import { ledigeTider, steamerPasser, type DagsOpgave, type Regler } from './ledige.ts';
import { rens, gyldigDato } from './validering.ts';
import { gyldigtPostnr } from './geo.ts';
import { iDag, minutterTil, tidsrum } from './dato.ts';
import { GEBYR_NAVN, datoGebyr, gebyrMoms, gebyrerFra, type GebyrIndst, type GebyrType } from './gebyrer.ts';
import { STANDARD_MEDLEM, type MedlemsIndst } from './medlemskab.ts';
import type { RuteFlytIndst } from './ruteoptimering.ts';

export const AKTIVE: BookingStatus[] = ['afventer', 'godkendt', 'i_gang', 'faerdig'];

export type BookingIndst = {
  godkendelse: 'manuel' | 'auto'; minVarselTimer: number; aflysFristTimer: number; trin: number; pause: number; maxKoersel: number;
  tidsmodel: 'fleksibel' | 'faste'; fasteTider: number[]; slotLaengde: number; bufferFoer: number; bufferEfter: number; maxOpgaver: number | null;
  ruteFlyt?: RuteFlytIndst;   // ruteoptimeringen må flytte kundernes tider (højst maxTimer fra kundens tid)
};
/** Det udsnit af klientens data, motoren bruger. bookinger behøver kun at dække de datoer, der regnes på. */
export type MotorData = {
  indst: { booking: BookingIndst; moms: { procent: number }; gebyrer?: Partial<GebyrIndst>; medlemskab?: MedlemsIndst };
  biltyper: Biltype[]; ydelser: Ydelse[]; priser: YdelsePris[]; tilvalg: Tilvalg[]; kuponer: Kupon[];
  steamere: Steamer[]; arbejdstider: Arbejdstid[]; saerligeDage: SaerligDag[]; fravaer: Fravaer[]; lukkedage: Lukkedag[]; zoner: Zone[];
  bookinger: (Pick<Booking, 'id' | 'dato' | 'steamer_id' | 'start' | 'slut' | 'status' | 'postnr'> & Partial<Pick<Booking, 'buffer_foer' | 'buffer_efter'>>)[];
};
export type BilValgPris = { biltype_id: string; ydelse_id: string; tilvalg?: ValgtTilvalg[] };
export type OverslagGebyr = { type: GebyrType; navn: string; beloeb: number; moms: number };
export type Overslag = { biler: (Prisberegning & { ydelse: string })[]; gebyrer: OverslagGebyr[]; subtotal: number; rabat: number; moms: number; total: number; varighed: number; kupon: string | null };

export function regler(d: Pick<MotorData, 'indst'>): Regler {
  const b = d.indst.booking;
  return { pause: b.pause, trin: b.trin, maxKoersel: b.maxKoersel, faste: b.tidsmodel === 'faste' ? b.fasteTider : null, bufferFoer: b.bufferFoer, bufferEfter: b.bufferEfter, maxOpgaver: b.maxOpgaver };
}
/** Med faste tider fylder en booking mindst én hel tid (fx 3 timer). */
export function tidsrumFor(d: MotorData, varighed: number): number {
  const b = d.indst.booking;
  return b.tidsmodel === 'faste' ? Math.max(varighed, b.slotLaengde) : varighed;
}
export function opgaver(d: MotorData, dato: string): DagsOpgave[] {
  return d.bookinger.filter((b) => b.dato === dato && b.steamer_id && b.start != null && b.slut != null && AKTIVE.includes(b.status))
    .map((b) => ({ id: b.id, steamer_id: b.steamer_id!, start: b.start!, slut: b.slut!, postnr: b.postnr, bF: b.buffer_foer ?? null, bE: b.buffer_efter ?? null }));
}
export const medlemsIndst = (d: MotorData) => d.indst.medlemskab ?? STANDARD_MEDLEM;
/** Medlemsrabat i % for et antal besøg om året, eller fejl hvis intervallet ikke må vælges. */
export function medlemsRabat(d: MotorData, besoeg: number): number {
  const m = medlemsIndst(d);
  if (!m.aktiv) throw fejl('Medlemskab kan ikke tegnes online lige nu.');
  const pct = m.rabat[String(besoeg)];
  if (pct == null || besoeg < m.minBesoeg) throw fejl(`Vælg et interval med mindst ${m.minBesoeg} vask om året.`);
  return pct;
}
export function bilPris(d: MotorData, b: BilValgPris, kupon: Kupon | null, kunOnline: boolean, medlemRabat = 0): Prisberegning & { ydelse: string } {
  const y = d.ydelser.find((x) => x.id === b.ydelse_id && x.aktiv && (!kunOnline || x.online));
  if (!y) throw fejl('Behandlingen findes ikke længere. Vælg en anden.');
  if (!d.biltyper.some((t) => t.id === b.biltype_id)) throw fejl('Vælg en biltype.');
  try {
    const p = beregnPris({ ydelseNavn: y.navn, pris: d.priser.find((x) => x.ydelse_id === y.id && x.biltype_id === b.biltype_id), tilvalg: d.tilvalg, valgte: b.tilvalg ?? [], kupon, momsProcent: d.indst.moms.procent, medlemRabat });
    return { ...p, ydelse: y.navn };
  } catch (e) { throw fejl((e as Error).message); }
}
export function overslag(d: MotorData, biler: BilValgPris[], kode: string | undefined, kunOnline: boolean, dato = iDag(), medlem: number | null = null): Overslag {
  const mRabat = medlem ? medlemsRabat(d, medlem) : 0;
  if (mRabat && rens(kode ?? '', 30)) throw fejl('Rabatkoder kan ikke bruges sammen med medlemspris.', { kupon: 'Kan ikke bruges sammen med medlemspris.' });
  if (!biler.length) throw fejl('Vælg mindst én bil.');
  if (biler.length > 10) throw fejl('Højst 10 biler i én bestilling. Ring til os ved flere.');
  let kupon: Kupon | null = null;
  const k = rens(kode ?? '', 30).toUpperCase();
  if (k) {
    const fk = d.kuponer.find((x) => x.kode === k);
    const f = kuponFejl(fk, dato);
    if (f) throw fejl(f, { kupon: f });
    kupon = fk!;
  }
  // Procentrabat gælder alle biler. Fast beløb gælder kun den første bil.
  const linjer = biler.map((b, i) => bilPris(d, b, kupon && (kupon.type === 'procent' || i === 0) ? kupon : null, kunOnline, mRabat));
  const sum = (f: (p: Prisberegning) => number) => linjer.reduce((s, p) => s + f(p), 0);
  return { biler: linjer, gebyrer: [], subtotal: sum((p) => p.subtotal), rabat: sum((p) => p.rabat), moms: sum((p) => p.moms), total: sum((p) => p.total), varighed: sum((p) => p.varighed), kupon: kupon?.kode ?? null };
}
export const gebyrIndst = (d: MotorData): GebyrIndst => gebyrerFra(d.indst.gebyrer);
/** Gebyrer for en bestilling fra hjemmesiden: weekend/helligdag (efter dato) og faktura (efter betalingsmåde). */
export function bestillingsGebyrer(d: MotorData, dato: string | null | undefined, betaling: 'link' | 'faktura' | undefined): { type: GebyrType; beloeb: number }[] {
  const g = gebyrIndst(d); const ud: { type: GebyrType; beloeb: number }[] = [];
  const dg = dato && gyldigDato(dato) ? datoGebyr(dato, g) : null;
  if (dg) ud.push(dg);
  if (betaling === 'faktura' && g.faktura > 0) ud.push({ type: 'faktura', beloeb: g.faktura });
  return ud;
}
export function medGebyrer(d: MotorData, o: Overslag, l: { type: GebyrType; beloeb: number }[]): Overslag {
  const mp = d.indst.moms.procent;
  const gebyrer = l.map((x) => ({ type: x.type, navn: GEBYR_NAVN[x.type], beloeb: x.beloeb, moms: gebyrMoms(x.type, x.beloeb, mp) }));
  const b = gebyrer.reduce((s, x) => s + x.beloeb, 0), m = gebyrer.reduce((s, x) => s + x.moms, 0);
  return { ...o, gebyrer, subtotal: o.subtotal + b, moms: o.moms + m, total: o.total + b + m };
}
/** Ledige tider til en hel bestilling. Bilerne laves efter hinanden af samme steamer. */
export function tiderFor(d: MotorData, dato: string, postnr: string, biler: BilValgPris[], ignorer?: number, kunOnline = true) {
  if (!gyldigDato(dato) || !gyldigtPostnr(postnr)) return [];
  const varighed = tidsrumFor(d, overslag(d, biler, undefined, kunOnline).varighed);
  const steamere = d.steamere.filter((s) => s.aktiv && biler.every((b) => steamerPasser(s, postnr, d.zoner, b.ydelse_id)));
  const opg = opgaver(d, dato).filter((o) => o.id !== ignorer);
  const minVarsel = d.indst.booking.minVarselTimer * 60;
  return ledigeTider({ steamere, dato, arbejdstider: d.arbejdstider, fravaer: d.fravaer, opgaver: opg, postnr, varighed, saerligeDage: d.saerligeDage, lukkedage: d.lukkedage.map((l) => l.dato), zoner: d.zoner, regler: regler(d) })
    .filter((t) => minutterTil(dato, t.start) >= minVarsel);
}
/** Antal ledige tider pr. dag (kalenderen på bookingsiden). */
export function ledigeDage(d: MotorData, fra: string, dage: number, postnr: string, biler: BilValgPris[]): Record<string, number> {
  const ud: Record<string, number> = {};
  const start = Date.parse(fra + 'T00:00:00Z');
  for (let i = 0; i < Math.min(dage, 62); i++) {
    const dato = new Date(start + i * 864e5).toISOString().slice(0, 10);
    ud[dato] = dato < iDag() ? 0 : tiderFor(d, dato, postnr, biler).length;
  }
  return ud;
}
/** Har steameren allerede en opgave i tidsrummet? Så kastes en forståelig fejl. */
export function tjekOverlap(d: MotorData, steamer_id: string, dato: string, start: number, slut: number, ignorer?: number) {
  const o = opgaver(d, dato).find((x) => x.steamer_id === steamer_id && x.id !== ignorer && x.start < slut && x.slut > start);
  if (o) {
    const s = d.steamere.find((x) => x.id === steamer_id)?.navn ?? 'Steameren';
    throw fejl(`${s} har allerede en opgave ${tidsrum(o.start, o.slut)} (booking ${o.id}). Vælg en anden tid eller steamer.`);
  }
}
